import { useT } from '../../i18n/useT'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'

/**
 * Said when detection cannot honestly name the language.
 *
 * Two cases, both of which used to pass in silence. `unsupported`: the model
 * is sure, and sure of a language we do not transcribe — the file used to go
 * through anyway and come back as whatever the gateway made of it, which
 * reads as "it picked the nearest language it knows". `unsure`: the guess IS
 * one of ours but the model is barely confident, which is what a language it
 * has never heard usually looks like — and the auto-detect path acted on
 * that guess without ever looking at the confidence it came with.
 *
 * Either way the point is to name what was heard rather than proceed quietly.
 */
interface LanguageUnsureModalProps {
  isOpen: boolean
  kind: 'unsupported' | 'unsure'
  detectedName: string
  confidence: number
  onChoose: () => void
  /** Offered only when there is something sensible to go ahead with */
  continueLabel?: string
  onContinue?: () => void
  onClose: () => void
}

export function LanguageUnsureModal({
  isOpen,
  kind,
  detectedName,
  confidence,
  onChoose,
  continueLabel,
  onContinue,
  onClose,
}: LanguageUnsureModalProps) {
  const t = useT()
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={kind === 'unsupported' ? t('unsure.titleUnsupported') : t('unsure.titleUnsure')}
    >
      <div className="text-center py-2">
        <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-text-secondary text-sm mb-6">
          {kind === 'unsupported'
            ? t('unsure.bodyUnsupported', { name: detectedName })
            : t('unsure.bodyUnsure', { name: detectedName, confidence: Math.round(confidence * 100) })}
        </p>
        <div className="flex gap-3">
          <Button
            variant={onContinue ? 'secondary' : 'primary'}
            onClick={onChoose}
            className="flex-1"
          >
            {t('unsure.choose')}
          </Button>
          {onContinue && continueLabel && (
            <Button onClick={onContinue} className="flex-1">
              {continueLabel}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
